import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ContentCard, ContentDetails, APIResponse, APIError, PersonSummaryWithPhoto } from '../types';
import { authService } from './auth';
import { getConfig, isProduction } from '../config';

// Get configuration
const config = getConfig();

// Enhanced API client configuration with environment-specific settings
const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: config.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Enable credentials for cross-origin requests in production
  withCredentials: isProduction(),
  // Add CORS configuration for deployed environments
  ...(isProduction() && {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Access-Control-Allow-Origin': config.allowedOrigins[0] || '*',
    }
  })
});

// Request ID generator for tracking
const generateRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Enhanced request interceptor with logging and request tracking
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const requestId = generateRequestId();
    config.metadata = { requestId, startTime: Date.now() };
    
    // Add request ID header for backend tracking
    config.headers['X-Request-ID'] = requestId;
    
    // Add environment-specific headers
    config.headers['X-Environment'] = getConfig().environment;
    
    // Add authentication token if available (use sync version for interceptor)
    const authToken = authService.getAuthTokenSync();
    if (authToken) {
      (config.headers as any)['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Add CORS headers for production deployment
    if (isProduction()) {
      config.headers['Origin'] = window.location.origin;
      // Ensure proper content type for CORS preflight
      if (config.method?.toLowerCase() !== 'get') {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    
    // Log request details in development
    if (getConfig().enableDebugMode) {
      console.log(`[API Request] ${requestId}: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
        hasAuth: !!authToken,
        environment: getConfig().environment,
      });
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]:', error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with error handling and retry logic
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const { config } = response;
    const requestId = config.metadata?.requestId;
    const duration = Date.now() - (config.metadata?.startTime || 0);
    
    // Log successful responses in development
    if (getConfig().enableDebugMode) {
      console.log(`[API Response] ${requestId}: ${response.status} (${duration}ms)`, {
        data: response.data,
      });
    }
    
    return response;
  },
  async (error: AxiosError<APIError>) => {
    const { config, response } = error;
    const requestId = config?.metadata?.requestId;
    const duration = Date.now() - (config?.metadata?.startTime || 0);
    
    // Enhanced error logging with environment context
    const errorContext = {
      url: config?.url,
      method: config?.method,
      status: response?.status,
      statusText: response?.statusText,
      data: response?.data,
      environment: getConfig().environment,
      isCorsError: !response && error.code === 'ERR_NETWORK',
    };
    
    console.error(`[API Error] ${requestId}: ${response?.status || 'Network Error'} (${duration}ms)`, errorContext);
    
    // Handle CORS errors specifically for deployed environments
    if (!response && error.code === 'ERR_NETWORK') {
      const corsError: APIError = {
        error: {
          code: 'CORS_ERROR',
          message: isProduction() 
            ? 'Unable to connect to the API. Please check your network connection.'
            : 'CORS error detected. This may be due to API configuration issues.',
          details: `Network error in ${getConfig().environment} environment: ${error.message}`,
        },
        timestamp: new Date().toISOString(),
        requestId: requestId || 'unknown',
      };
      
      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: corsError,
        },
      });
    }
    
    // Handle specific error cases
    if (response?.status === 429) {
      // Rate limiting - implement exponential backoff
      const retryAfter = parseInt(response.headers['retry-after'] || '1', 10);
      const delay = Math.min(retryAfter * 1000, 5000); // Max 5 seconds
      
      console.warn(`[API] Rate limit exceeded. Retrying after ${delay}ms...`);
      
      // Only retry if we haven't exceeded max retries
      const retryCount = (config as any).__retryCount || 0;
      if (retryCount < getConfig().maxRetries) {
        (config as any).__retryCount = retryCount + 1;
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return apiClient.request(config!);
      }
    }
    
    // Handle authentication errors (401/403)
    if (response?.status === 401 || response?.status === 403) {
      // Clear auth token and redirect to login in production
      if (isProduction()) {
        authService.logout();
        // Don't redirect automatically in development for debugging
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    
    // Handle network errors
    if (!response) {
      const networkError: APIError = {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Network connection failed. Please check your internet connection.',
          details: error.message,
        },
        timestamp: new Date().toISOString(),
        requestId: requestId || 'unknown',
      };
      
      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: networkError,
        },
      });
    }
    
    // Handle server errors (5xx)
    if (response.status >= 500) {
      const serverError: APIError = {
        error: {
          code: 'SERVER_ERROR',
          message: 'Server error occurred. Please try again later.',
          details: response.data?.error?.details || response.statusText,
        },
        timestamp: new Date().toISOString(),
        requestId: requestId || 'unknown',
      };
      
      return Promise.reject({
        ...error,
        response: {
          ...response,
          data: serverError,
        },
      });
    }
    
    // Handle client errors (4xx) - pass through as-is for specific handling
    return Promise.reject(error);
  }
);

// Retry utility function with exponential backoff
const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on client errors (4xx) except 429
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && status !== 429) {
          throw error;
        }
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.log(`[API] Retrying request in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
};

// Enhanced API methods with retry logic and better error handling
export const contentApi = {
  getFeaturedContent: async (): Promise<ContentCard[]> => {
    return retryRequest(async () => {
      const response = await apiClient.get<APIResponse<ContentCard[]>>('/api/content/featured');
      
      if (response.data && typeof response.data === 'object') {
        // Handle new format: { data: [...], metadata: {...} }
        if ('data' in response.data) {
          return response.data.data || [];
        }
        
        // Handle old format: { featuredContent: [...], category: "...", count: ... }
        if ('featuredContent' in response.data) {
          return (response.data as any).featuredContent || [];
        }
        
        // Fallback: if response.data is directly an array
        if (Array.isArray(response.data)) {
          return response.data;
        }
      }
      
      return [];
    });
  },

  getMovies: async (
    page = 1, 
    limit = 20, 
    filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string>; includePeople?: boolean }
  ): Promise<APIResponse<ContentCard[]>> => {
    return retryRequest(async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        // Note: Backend uses nextToken for pagination, not page numbers
        ...(filters?.genre && { genre: filters.genre }),
        ...(filters?.year && { year: filters.year.toString() }),
        ...(filters?.yearFrom && { yearFrom: filters.yearFrom.toString() }),
        ...(filters?.yearTo && { yearTo: filters.yearTo.toString() }),
        ...(filters?.sortBy && { sortBy: filters.sortBy }),
        ...(filters?.includePeople && { includePeople: 'true' }),
      });
      
      const response = await apiClient.get(`/api/content/movies?${params}`);
      
      // Handle backend response format: { movies: [...], count: ..., filters: ... }
      if (response.data && typeof response.data === 'object') {
        if ('movies' in response.data) {
          let movies = response.data.movies || [];
          
          // Log people data inclusion for debugging
          if (filters?.includePeople && getConfig().enableDebugMode) {
            const moviesWithPeople = movies.filter((movie: ContentCard) => movie.people);
            console.log(`[API] Movies with people data: ${moviesWithPeople.length}/${movies.length}`);
          }
          
          // Validate people data if included
          if (filters?.includePeople) {
            movies = movies.map(validatePeopleData);
          }
          
          // Apply client-side filtering if popularTitleIds is provided
          if (filters?.popularTitleIds && filters.popularTitleIds.size > 0) {
            movies = movies.filter((movie: ContentCard) => filters.popularTitleIds!.has(movie.id));
          }
          
          return {
            data: movies,
            pagination: {
              page,
              limit,
              total: response.data.count || 0,
              hasMore: (movies.length || 0) === limit
            }
          };
        }
        
        // Fallback to standard format
        if ('data' in response.data) {
          let data = response.data.data || [];
          
          // Validate people data if included
          if (filters?.includePeople) {
            data = data.map(validatePeopleData);
          }
          
          // Apply client-side filtering if popularTitleIds is provided
          if (filters?.popularTitleIds && filters.popularTitleIds.size > 0) {
            data = data.filter((movie: ContentCard) => filters.popularTitleIds!.has(movie.id));
          }
          
          return {
            ...response.data,
            data,
          };
        }
      }
      
      return { data: [] };
    });
  },

  getTVShows: async (
    page = 1, 
    limit = 20, 
    filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string>; includePeople?: boolean }
  ): Promise<APIResponse<ContentCard[]>> => {
    return retryRequest(async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        // Note: Backend uses nextToken for pagination, not page numbers
        ...(filters?.genre && { genre: filters.genre }),
        ...(filters?.year && { year: filters.year.toString() }),
        ...(filters?.yearFrom && { yearFrom: filters.yearFrom.toString() }),
        ...(filters?.yearTo && { yearTo: filters.yearTo.toString() }),
        ...(filters?.sortBy && { sortBy: filters.sortBy }),
        ...(filters?.includePeople && { includePeople: 'true' }),
      });
      const response = await apiClient.get(`/api/content/tv-shows?${params}`);
      
      // Handle backend response format: { tvShows: [...], count: ..., filters: ... }
      if (response.data && typeof response.data === 'object') {
        if ('tvShows' in response.data) {
          let tvShows = response.data.tvShows || [];
          
          // Log people data inclusion for debugging
          if (filters?.includePeople && getConfig().enableDebugMode) {
            const showsWithPeople = tvShows.filter((show: ContentCard) => show.people);
            console.log(`[API] TV Shows with people data: ${showsWithPeople.length}/${tvShows.length}`);
          }
          
          // Validate people data if included
          if (filters?.includePeople) {
            tvShows = tvShows.map(validatePeopleData);
          }
          
          // Apply client-side filtering if popularTitleIds is provided
          if (filters?.popularTitleIds && filters.popularTitleIds.size > 0) {
            tvShows = tvShows.filter((show: ContentCard) => filters.popularTitleIds!.has(show.id));
          }
          
          return {
            data: tvShows,
            pagination: {
              page,
              limit,
              total: response.data.count || 0,
              hasMore: (tvShows.length || 0) === limit
            }
          };
        }
        
        // Fallback to standard format
        if ('data' in response.data) {
          let data = response.data.data || [];
          
          // Validate people data if included
          if (filters?.includePeople) {
            data = data.map(validatePeopleData);
          }
          
          // Apply client-side filtering if popularTitleIds is provided
          if (filters?.popularTitleIds && filters.popularTitleIds.size > 0) {
            data = data.filter((show: ContentCard) => filters.popularTitleIds!.has(show.id));
          }
          
          return {
            ...response.data,
            data,
          };
        }
      }
      
      return { data: [] };
    });
  },

  getContentDetails: async (id: string): Promise<ContentDetails> => {
    if (!id || typeof id !== 'string') {
      throw new Error('Content ID is required and must be a string');
    }
    
    return retryRequest(async () => {
      const response = await apiClient.get(`/api/content/${encodeURIComponent(id)}`);
      
      // Handle backend response format: { content: {...}, requestedAt: "..." }
      if (response.data && typeof response.data === 'object') {
        if ('content' in response.data) {
          return response.data.content;
        }
        
        // Fallback to standard format
        if ('data' in response.data) {
          return response.data.data;
        }
      }
      
      throw new Error('Invalid response format');
    });
  },

  searchContent: async (
    query: string, 
    page = 1, 
    limit = 20
  ): Promise<APIResponse<ContentCard[]>> => {
    if (!query || typeof query !== 'string' || query.trim().length === 0) {
      throw new Error('Search query is required and must be a non-empty string');
    }
    
    return retryRequest(async () => {
      const params = new URLSearchParams({
        q: query.trim(),
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await apiClient.get<APIResponse<ContentCard[]>>(`/api/content/search?${params}`);
      return response.data;
    });
  },

  getGenres: async (): Promise<string[]> => {
    return retryRequest(async () => {
      const response = await apiClient.get<APIResponse<string[]>>('/api/genres');
      return response.data.data;
    });
  },

  getPersonDetails: async (id: string): Promise<any> => {
    if (!id || typeof id !== 'string') {
      throw new Error('Person ID is required and must be a string');
    }
    
    return retryRequest(async () => {
      const response = await apiClient.get(`/api/persons/${encodeURIComponent(id)}`);
      
      // Handle backend response format: { data: {...}, requestedAt: "..." }
      if (response.data && typeof response.data === 'object') {
        if ('data' in response.data) {
          return response.data.data;
        }
      }
      
      throw new Error('Invalid response format');
    });
  },

  // Additional utility methods for better API management
  getContentByGenre: async (
    genre: string, 
    page = 1, 
    limit = 20
  ): Promise<APIResponse<ContentCard[]>> => {
    if (!genre || typeof genre !== 'string') {
      throw new Error('Genre is required and must be a string');
    }
    
    return retryRequest(async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await apiClient.get<APIResponse<ContentCard[]>>(
        `/api/content/by-genre/${encodeURIComponent(genre)}?${params}`
      );
      return response.data;
    });
  },

  getContentByYear: async (
    year: number, 
    page = 1, 
    limit = 20
  ): Promise<APIResponse<ContentCard[]>> => {
    if (!year || typeof year !== 'number' || year < 1900 || year > new Date().getFullYear() + 5) {
      throw new Error('Year must be a valid number between 1900 and current year + 5');
    }
    
    return retryRequest(async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await apiClient.get<APIResponse<ContentCard[]>>(
        `/api/content/by-year/${year}?${params}`
      );
      return response.data;
    });
  },

  getTopPopular: async (
    limit = 20,
    type?: 'movie' | 'tvSeries'
  ): Promise<{ titles: ContentCard[]; nextToken?: string; count: number; filters: { type?: string; limit: number } }> => {
    if (limit < 1 || limit > 500) {
      throw new Error('Limit must be between 1 and 500');
    }
    
    if (type && !['movie', 'tvSeries'].includes(type)) {
      throw new Error('Type must be either "movie" or "tvSeries"');
    }
    
    return retryRequest(async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        ...(type && { type }),
      });
      const response = await apiClient.get(
        `/api/titles/top-popular?${params}`
      );
      
      // Handle backend response format: { titles: [...], nextToken: "...", count: ..., filters: {...} }
      if (response.data && typeof response.data === 'object') {
        const { titles = [], nextToken, count = 0, filters } = response.data;
        
        if (!Array.isArray(titles)) {
          throw new Error('Expected titles to be an array');
        }
        
        return {
          titles,
          nextToken,
          count,
          filters: filters || { limit, type },
        };
      }
      
      throw new Error('Invalid response format');
    });
  },

  getTop500PopularTitles: async (): Promise<{ titles: ContentCard[]; titleIds: Set<string> }> => {
    return retryRequest(async () => {
      const response = await apiClient.get('/api/titles/top-popular?limit=500');
      
      // Handle backend response format: { titles: [...], nextToken: "...", count: ..., filters: {...} }
      if (response.data && typeof response.data === 'object') {
        const { titles = [] } = response.data;
        
        if (!Array.isArray(titles)) {
          throw new Error('Expected titles to be an array');
        }
        
        // Create a Set of title IDs for efficient filtering
        const titleIds = new Set(titles.map(title => title.id));
        
        return {
          titles,
          titleIds,
        };
      }
      
      throw new Error('Invalid response format');
    });
  },
};

// People data validation utility
const validatePeopleData = (contentItem: ContentCard): ContentCard => {
  if (contentItem.people) {
    // Ensure people data structure is valid
    const { cast = [], directors = [], writers = [] } = contentItem.people;
    
    // Validate each person has required fields
    const validatePersonArray = (people: PersonSummaryWithPhoto[]) => {
      return people.filter(person => person.id && person.name);
    };
    
    contentItem.people = {
      cast: validatePersonArray(cast),
      directors: validatePersonArray(directors),
      writers: validatePersonArray(writers),
    };
  }
  
  return contentItem;
};

// Error handling utilities
export const isAPIError = (error: any): error is AxiosError<APIError> => {
  return error?.response?.data?.error !== undefined;
};

export const getErrorMessage = (error: any): string => {
  if (isAPIError(error)) {
    return error.response?.data?.error?.message || 'An unexpected error occurred';
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

export const getErrorCode = (error: any): string => {
  if (isAPIError(error)) {
    return error.response?.data?.error?.code || 'UNKNOWN_ERROR';
  }
  
  return 'UNKNOWN_ERROR';
};

export const isNetworkError = (error: any): boolean => {
  return !error?.response && error?.request;
};

export const isServerError = (error: any): boolean => {
  return error?.response?.status >= 500;
};

export const isClientError = (error: any): boolean => {
  const status = error?.response?.status;
  return status >= 400 && status < 500;
};

// People data utilities
export const hasPeopleData = (contentItem: ContentCard): boolean => {
  return !!(contentItem.people && (
    contentItem.people.cast.length > 0 ||
    contentItem.people.directors.length > 0 ||
    contentItem.people.writers.length > 0
  ));
};

export const getPeopleCount = (contentItem: ContentCard): number => {
  if (!contentItem.people) return 0;
  
  return (
    contentItem.people.cast.length +
    contentItem.people.directors.length +
    contentItem.people.writers.length
  );
};

// Admin API methods
export const adminApi = {
  importData: async (params: {
    titleJsonBucket: string;
    titleJsonKey: string;
    personJsonBucket: string;
    personJsonKey: string;
    dataType?: 'both' | 'title' | 'person';
  }) => {
    return retryRequest(async () => {
      const response = await apiClient.post('/api/admin/import', params);
      return response.data;
    });
  },
};

// Environment-specific API configuration utilities
export const apiConfig = {
  /**
   * Gets the current API base URL for the environment
   */
  getBaseUrl: (): string => {
    return getConfig().apiBaseUrl;
  },

  /**
   * Validates API configuration for the current environment
   */
  validateConfig: (): { isValid: boolean; errors: string[] } => {
    const config = getConfig();
    const errors: string[] = [];

    if (!config.apiBaseUrl) {
      errors.push('API base URL is not configured');
    }

    if (!config.apiBaseUrl.startsWith('https://') && isProduction()) {
      errors.push('API base URL must use HTTPS in production');
    }

    if (config.apiTimeout < 1000) {
      errors.push('API timeout should be at least 1000ms');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Gets environment-specific request configuration
   */
  getRequestConfig: (additionalConfig?: any) => {
    const baseConfig = {
      timeout: getConfig().apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Environment': getConfig().environment,
      },
    };

    // Add authentication if available
    const authToken = authService.getAuthTokenSync();
    if (authToken) {
      (baseConfig.headers as any)['Authorization'] = `Bearer ${authToken}`;
    }

    // Merge with additional config
    return { ...baseConfig, ...additionalConfig };
  },
};

// Enhanced error handling utilities for deployed environments
export const deploymentErrorHandlers = {
  /**
   * Handles CORS-related errors in deployed environments
   */
  handleCorsError: (error: AxiosError): APIError => {
    return {
      error: {
        code: 'CORS_ERROR',
        message: isProduction() 
          ? 'Unable to connect to the API. Please try again later.'
          : 'CORS configuration issue detected. Check API Gateway CORS settings.',
        details: `CORS error in ${getConfig().environment}: ${error.message}`,
      },
      timestamp: new Date().toISOString(),
      requestId: 'cors-error',
    };
  },

  /**
   * Handles authentication errors in deployed environments
   */
  handleAuthError: (_error: AxiosError): void => {
    console.warn('[API] Authentication error detected, clearing session');
    authService.logout();
    
    // Only redirect in production to avoid disrupting development
    if (isProduction() && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  },

  /**
   * Determines if an error is retryable based on environment
   */
  isRetryableError: (error: AxiosError): boolean => {
    const status = error.response?.status;
    
    // Don't retry client errors except rate limiting
    if (status && status >= 400 && status < 500 && status !== 429) {
      return false;
    }
    
    // Don't retry CORS errors in production (likely configuration issue)
    if (!error.response && error.code === 'ERR_NETWORK' && isProduction()) {
      return false;
    }
    
    return true;
  },
};

// API client health check with environment-specific validation
export const healthCheck = async (): Promise<{ 
  status: 'healthy' | 'unhealthy'; 
  timestamp: string; 
  environment: string;
  configValid: boolean;
}> => {
  const configValidation = apiConfig.validateConfig();
  
  try {
    await apiClient.get('/health', { timeout: 5000 });
    return {
      status: 'healthy',
      timestamp: new Date().toISOString(),
      environment: getConfig().environment,
      configValid: configValidation.isValid,
    };
  } catch (error) {
    console.error('[API Health Check] Failed:', error);
    return {
      status: 'unhealthy',
      timestamp: new Date().toISOString(),
      environment: getConfig().environment,
      configValid: configValidation.isValid,
    };
  }
};

export default apiClient;